// Card 69, the points of interest (R2626, R2714, ui-map-interface.md
// §17.3.69, v2-the-deep.md §4.2).
//
// Each point of interest that the survey of the viewer's faction holds
// wears the mark of the Marks field `poi`: one of the five sets of mockup
// round 114 (CHART ABBREVIATION, the AUTO set; SEA BED; DOT AND LABEL;
// REACH; SHAPE GRAMMAR). One set draws all five kinds, a vent, a seeded
// cache, a lair, a current gate and a ruin.
//
// **IT DRAWS WHAT THE CLIENT KNOWS** (v2 §14.1 rule 5). The points are the
// known points of the survey store, which the wire fills only while the flag
// `discovery` is latched. A seeded cache shows while the page says the world
// plays the treasure (`ChartContext.cachesOn`). The state of a point comes
// from what the client holds: the tide and the current of its cell for a
// current gate (flood, slack, ebb), an own hull that works a SEARCH on the
// cell for a seeded cache and a ruin, and the own finds of the Empire for a
// dug cache and a taken Data Core. The live rows of the wire (R2735) give
// the stock of a vent, the monster of a lair and a cache that any commander
// dug; the server sends a row only while the commander hears the point, so
// the piece draws the last row the client saw, and the ready look of a
// point it never heard. A timer (the refill of a vent, the respawn of a
// monster) is a figure that the piece writes again on each world minute,
// and on each world second while it counts under an hour (R2741).
//
// **THREE OBJECTS, ONE PIECE.** The registry holds `points_of_interest`
// three times; it holds the map markers twice. The STATIC object packs the baked marks
// (`render/poi-marks.ts`), the words, the reach of a lair and of a gate, on
// a reason; the LIVE object draws what moves, the specks, the flash and the
// stream of SEA BED and the orbit dot of SHAPE GRAMMAR, and only while one
// of those two sets is picked (§10.3).
//
// **THE AREA OF A GATE** (R2955). A third object draws the area of ONE
// current gate, the gate whose card the peek shows after a hover or a
// press, as a dashed hex outline in the gate ink: the line of the harbour
// limits of card 28. The area is the server's own set (`gateCells` of
// `world/currents.go`), so a ridge stops it. The REACH set draws the
// same set, ring by ring (`gateRings`, R2958).
//
// **A SMALL MARK** (R2851). The piece answers no `pick`, so
// `commands.clickMap` never finds a point. The static object answers
// `markPick`: a square of `fig.pick_half_px` round the centre of the cell
// of a point of the survey, grown by the touch margin of a finger, holds
// the press. The tooltip peeks the card of the point on a hover and on a
// press (`fast/smallmarks.ts`). A row of the admin list takes no press.

import { type AdminCache, CacheKind, Mission, PoiKind, PoiMonster, type PointOfInterest } from '../../../gen/proto/aquaphobia';
import { chart as S } from '../../../gen/strings';
import type { CHART } from '../../../gen/presentation';
import { BLOCK_RIDGE, HEX_SIZE, POI_STYLES } from '../../../gen/balance';
import { poiShown } from '../../../survey';
import { leftSpan, leftTicksBySecond } from '../../../locale/span';
import { currentAt } from '../../../deep';
import { reducedMotion } from '../../../render/motion';
import { POI_INKS } from '../../../render/poi-cells';
import {
  POI_HEX_KEY, POI_KIND_SLUGS, abbrArrowPx, gateState, poiBakesNothing, poiCellKey, poiHue, shareSteps,
  tideSteps,
  type PoiKindSlug, type PoiSet, type PoiState,
} from '../../../render/poi-marks';
import { poiRules } from '../../../render/poi-rules';
import { drawKindMark, monsterDesignWord, monsterKindOf } from '../../../render/monster-marks';
import { BatchPen } from '../../layers/monster-pen';
import { cellAt, cellCenterX, cellCenterY, hexCorner, mulberry32, neighborAt } from '../../../world';
import { F_SCREEN, type FrameInfo } from '../../layer';
import type { ChartContext, ChartSinks, ChartTier, Piece, RebuildReason } from '../context';
import { CHART_INK, dashedStrokes, faceOf, offCull, outlineStrokes, type Stroke } from './shared';
import { boxHolds, offerSmallMark, type SmallMark } from '../../smallmarks';

/** The AUTO set: position 0 of the list. */
const AUTO_SET: PoiSet = POI_STYLES[0];
/** The sets with a part that moves. */
const LIVE_SETS: ReadonlySet<PoiSet> = new Set<PoiSet>(['sea-bed', 'shape-grammar']);
/** A baked mark is drawn in its own inks. */
const WHITE = 0xffffff;
/** The cull margin of the live parts: the widest reach of a stream. */
const CULL_MARGIN = 48;
/** The dash of the lair's home on card 08: 2 on, 4 off. */
const LAIR_DASH_ON = 1 / 3;
/** The speck seed of a vent: the generator of the card page. */
const SPECK_SEED = 97;

type Fig = typeof CHART.pieces.points_of_interest.fig;

/** One speck of a SEA BED vent: its offset across and its phase. */
interface Speck { x0: number; ph: number }

/** One point as the piece draws it. The tests read them. */
export interface DrawnPoint {
  /** The id of the point, or the cache id of a row of the admin list. */
  id: number;
  kind: PoiKindSlug;
  cell: number;
  x: number;
  y: number;
  state: PoiState;
  /** The tide factor of a current gate, and 1 for every other kind. */
  tide: number;
  /** The turn of the mark: the flow of a current gate, and 0 otherwise. */
  rot: number;
  /** The step of the baked cell: the tide step of a gate, the stock step
   *  of a vent at work, and 0 otherwise. */
  m: number;
  /** The share of its full stock a vent holds, 1 for every other kind. */
  share: number;
  /** The stock of a vent in isotopes, or -1 while the client heard none. */
  stock: number;
  /** The world seconds of the timer of a worked-out vent or a gone
   *  monster, 0 with none. */
  left: number;
  /** The specks of a SEA BED vent. */
  specks: Speck[];
  /** The monster kind of a lair, 0 to 4, and its design (R2719); -1 and 0
   *  on every other point and on a lair of a world without `pirates`. */
  monster: number;
  design: number;
  /** The point of the survey, which a pick hands to the tooltip (R2851);
   *  null on a row of the admin list. */
  poi: PointOfInterest | null;
}

/** The kind of the own find that spends a point, or 0 for a kind no find
 *  spends. */
function findKindOf(kind: PoiKindSlug): CacheKind {
  if (kind === 'cache') return CacheKind.CACHE_KIND_SEEDED;
  if (kind === 'ruin') return CacheKind.CACHE_KIND_RUIN;
  return CacheKind.CACHE_KIND_UNSPECIFIED;
}

/**
 * The rows of TOGGLE CACHES of the admin panel (R2825), after the points of
 * the survey: every cache of the season in the mark of a seeded cache, and
 * a ruin in the mark of a ruin. A seeded cache or a ruin that the survey
 * already draws on its cell does not draw twice. A taken cache draws spent.
 * The list changes only on a State frame that carries it, so the scene
 * packs again on the reason `survey` (§10).
 */
export function adminCaches(rows: readonly AdminCache[], out: DrawnPoint[]): void {
  if (rows.length === 0) return;
  const drawn = new Set<string>();
  for (const d of out) if (d.kind === 'cache' || d.kind === 'ruin') drawn.add(`${d.kind}:${d.cell}`);
  for (const r of rows) {
    const kind: PoiKindSlug = r.kind === CacheKind.CACHE_KIND_RUIN ? 'ruin' : 'cache';
    const surveyKind = r.kind === CacheKind.CACHE_KIND_SEEDED || r.kind === CacheKind.CACHE_KIND_RUIN;
    if (surveyKind && drawn.has(`${kind}:${r.cell}`)) continue;
    out.push({
      id: r.id, kind, cell: r.cell, x: cellCenterX(r.cell), y: cellCenterY(r.cell),
      state: r.taken ? 'spent' : 'ready', tide: 1, rot: 0, m: 0, share: 1, stock: -1, left: 0, specks: [],
      monster: -1, design: 0, poi: null,
    });
  }
}

/** Does a point of this kind show? The four of phase 3 always, and a
 *  seeded cache while the world plays the treasure (R2714). */
function shows(kind: PoiKind, cachesOn: boolean): boolean {
  return poiShown(kind) || (kind === PoiKind.POI_KIND_CACHE && cachesOn);
}

/**
 * The points of one pack and what they stand for, shared by the static and
 * the live object. It keeps the Empire frame, the flag and the zoom side it
 * built at, so `outgrown` is three compares.
 */
export class PoiModel {
  /** The points of the last build. */
  points: DrawnPoint[] = [];
  /** The set they are drawn in. */
  set: PoiSet = AUTO_SET;
  /** Reduced motion, read at the build and not on every frame. */
  still = false;
  /** The zoom is under the SEA BED dot zoom. */
  small = false;
  private empire: unknown = null;
  private caches = false;
  /** A point of the build writes a timer, and the world minute it read.
   *  A timer under an hour reads its seconds (R2741), so it writes again
   *  on the world second it read. */
  private timed = false;
  private bySecond = false;
  private minute = -1;
  private second = -1;

  /** Builds the points from the state. */
  build(ctx: ChartContext): void {
    const fig = ctx.cfg.pieces.points_of_interest.fig;
    const s = ctx.state;
    this.set = ctx.scene.poi ?? AUTO_SET;
    this.still = reducedMotion();
    this.small = ctx.zoom < fig.bed_dot_zoom;
    this.empire = s.empire;
    this.caches = ctx.cachesOn;
    const field = s.currents;
    const tideNow = field ? s.tideFactor() : 1;
    const finds = s.empire?.cacheFinds ?? [];
    const fleets = ctx.scene.fleets?.chartFleets() ?? [];
    const full = poiRules().ventStock;
    this.timed = false;
    this.bySecond = false;
    this.minute = Math.floor(ctx.worldClockS / 60);
    this.second = Math.floor(ctx.worldClockS);
    const flow: [number, number] = [0, 0];
    const out: DrawnPoint[] = [];
    for (const p of s.survey.pois) {
      if (!shows(p.kind, ctx.cachesOn)) continue;
      const kind = POI_KIND_SLUGS[p.kind - 1];
      if (!kind) continue;
      const d: DrawnPoint = {
        id: p.id, kind, cell: p.cell, x: cellCenterX(p.cell), y: cellCenterY(p.cell),
        state: 'ready', tide: 1, rot: 0, m: 0, share: 1, stock: -1, left: 0, specks: [],
        monster: kind === 'lair' ? monsterKindOf(p.monster) : -1, design: p.design, poi: p,
      };
      const seen = s.survey.live.get(p.id);
      if (kind === 'gate') {
        currentAt(field, p.cell, s.rules.currentUnitPxH, flow);
        const along = flow[0] !== 0 || flow[1] !== 0 ? Math.atan2(flow[1], flow[0]) : 0;
        d.tide = tideNow;
        d.state = gateState(tideNow, fig);
        // A gate at slack bakes one cell, whatever the factor.
        d.m = d.state === 'worked' ? 0 : tideSteps(tideNow, fig);
        d.rot = along + (tideNow < 0 ? Math.PI : 0);
      } else if (kind === 'cache' || kind === 'ruin') {
        const find = findKindOf(kind);
        if (seen?.row.dug || finds.some((f) => f.cell === p.cell && f.kind === find)) d.state = 'spent';
        else if (fleets.some((f) => f.own && f.mission === Mission.MISSION_SEARCH
          && cellAt(f.tx, f.ty) === p.cell && cellAt(f.x, f.y) === p.cell)) d.state = 'worked';
      } else if (kind === 'vent') {
        const r = mulberry32(p.id * SPECK_SEED + 3);
        for (let i = 0; i < fig.bed_specks; i++) d.specks.push({ ph: r(), x0: (r() - 0.5) * fig.bed_speck_spread_px });
        // The stock of the last row, as a share of the full stock of the
        // hello. With no row or no full stock the vent reads full.
        if (seen && full > 0) {
          d.stock = seen.row.stock;
          d.share = Math.min(1, d.stock / full);
          if (d.stock === 0) {
            d.state = 'spent';
            d.left = s.poiLeftSec(seen);
          } else if (d.stock < full) {
            d.state = 'worked';
            d.m = shareSteps(d.share, fig);
          }
        }
      } else if (kind === 'lair' && seen) {
        if (seen.row.monster === PoiMonster.POI_MONSTER_OUT) d.state = 'worked';
        else if (seen.row.monster === PoiMonster.POI_MONSTER_GONE) {
          d.state = 'spent';
          d.left = s.poiLeftSec(seen);
        }
      }
      if (d.left > 0) {
        this.timed = true;
        if (leftTicksBySecond(d.left)) this.bySecond = true;
      }
      out.push(d);
    }
    adminCaches(s.adminCaches, out);
    this.points = out;
  }

  /** A new Empire frame, the edge of the treasure flag, a new world minute
   *  while a timer stands (a new world second under an hour), or the zoom across the SEA BED dot zoom: an edge
   *  no reason names, one compare each. */
  outgrown(ctx: ChartContext): boolean {
    if (ctx.state.empire !== this.empire || ctx.cachesOn !== this.caches) return true;
    if (this.timed && Math.floor(ctx.worldClockS / 60) !== this.minute) return true;
    if (this.bySecond && Math.floor(ctx.worldClockS) !== this.second) return true;
    return this.set === 'sea-bed'
      && (ctx.zoom < ctx.cfg.pieces.points_of_interest.fig.bed_dot_zoom) !== this.small;
  }
}

/** The factor of a gate now, as a word writes it: the multiplier times the
 *  tide, one decimal. */
function mulText(tide: number): string {
  return (poiRules().gateMul * Math.abs(tide)).toFixed(1);
}


/** The figure of a timer of a vent or a lair, centred `dy` px under the
 *  mark (SEA BED, REACH, SHAPE GRAMMAR; round 114). */
function leftFigure(
  sinks: ChartSinks, ctx: ChartContext, d: DrawnPoint, dy: readonly number[], ink: number, a: number,
): void {
  if (d.left <= 0 || (d.kind !== 'vent' && d.kind !== 'lair')) return;
  const t = ctx.cfg.pieces.points_of_interest;
  const face = faceOf(ctx.reading);
  // A lair with a design name writes the timer under the name (R2719).
  const y = d.kind === 'vent' ? dy[0]
    : designed(d) ? Math.max(dy[1], t.fig.lair_design_dy + t.fig.lair_state_dy) : dy[1];
  ctx.glyphs.word(sinks.quads, leftSpan(d.left), d.x, d.y + y, face, t.text.note,
    ink, t.fig.left_alpha * a, { anchor: 'centre' });
}

/** A lair whose monster has a design name on the chart (R2719). */
function designed(d: DrawnPoint): boolean {
  return d.kind === 'lair' && d.monster >= 0 && monsterDesignWord(d.monster, d.design) !== '';
}

// ---------------------------------------------------------------------------
// The static object
// ---------------------------------------------------------------------------

/** The words of CHART ABBREVIATION (card 01) beside one point. */
function abbrWords(sinks: ChartSinks, ctx: ChartContext, d: DrawnPoint, fig: Fig, a: number): void {
  const t = ctx.cfg.pieces.points_of_interest;
  const dx = t.arr.abbr_name_dx;
  const face = faceOf(ctx.reading);
  const ink = CHART_INK.pale;
  const spent = d.state === 'spent' && d.kind !== 'gate';
  const na = (spent ? fig.abbr_spent_alpha : fig.abbr_name_alpha) * a, nota = fig.abbr_note_alpha * a;
  const name = (text: string, x: number, y: number): void => {
    ctx.glyphs.word(sinks.quads, text, d.x + x, d.y + y, face, t.text.name, ink, na, { anchor: 'left' });
  };
  const note = (text: string, x: number): void => {
    ctx.glyphs.word(sinks.quads, text, d.x + x, d.y + fig.abbr_name_dy + fig.abbr_note_dy, face, t.text.note,
      ink, nota, { anchor: 'left' });
  };
  switch (d.kind) {
    case 'vent':
      name(S.poi_abbr_vent, dx[0], fig.abbr_name_dy);
      if (d.state === 'worked') note(S.poi_abbr_left({ n: d.stock }), dx[0]);
      if (d.state === 'spent') note(d.left > 0 ? S.poi_abbr_dry({ left: leftSpan(d.left) }) : S.poi_abbr_empty, dx[0]);
      break;
    case 'cache':
      name(S.poi_abbr_cache, dx[1], fig.abbr_name_dy);
      if (d.state === 'worked') note(S.poi_abbr_search, dx[1]);
      if (d.state === 'spent') note(S.poi_abbr_dug, dx[1]);
      break;
    case 'lair': {
      name(S.poi_abbr_lair, dx[2], fig.abbr_name_dy);
      let word = '';
      if (d.state === 'worked') word = S.poi_abbr_empty;
      if (d.state === 'spent') word = d.left > 0 ? S.poi_abbr_empty_left({ left: leftSpan(d.left) }) : S.poi_abbr_empty;
      // Under the design name of the monster, when one stands (R2719).
      if (word && designed(d)) {
        ctx.glyphs.word(sinks.quads, word, d.x, d.y + fig.lair_design_dy + fig.lair_state_dy, face, t.text.note,
          ink, nota, { anchor: 'centre' });
      } else if (word) note(word, dx[2]);
      break;
    }
    case 'gate': {
      if (d.state === 'worked') {
        name(S.poi_abbr_gate, dx[3], fig.abbr_name_dy);
        note(S.poi_abbr_slack, dx[3]);
        break;
      }
      const x = abbrArrowPx(Math.abs(d.tide), fig) / 2 + fig.abbr_gate_gap;
      name(S.poi_abbr_gate, x, fig.abbr_gate_dy);
      const rate = d.tide > 0 ? S.poi_abbr_flood({ mul: mulText(d.tide) }) : S.poi_abbr_ebb({ mul: mulText(d.tide) });
      ctx.glyphs.word(sinks.quads, rate, d.x + x, d.y + fig.abbr_gate_dy + fig.abbr_rate_dy, face, t.text.note,
        ink, nota, { anchor: 'left' });
      break;
    }
    default:
      // The abbreviation stands in the foundation of the ruin.
      ctx.glyphs.word(sinks.quads, S.poi_abbr_ruin, d.x, d.y + fig.abbr_name_dy, face, t.text.name, ink, na,
        { anchor: 'centre' });
      if (d.state === 'worked') note(S.poi_abbr_search, dx[4]);
      if (d.state === 'spent') note(S.poi_abbr_taken, dx[4]);
      break;
  }
}

/** The label of DOT AND LABEL (card 06): the kind in the pale ink and its
 *  fact in the dim ink, and the own-cyan bar of a SEARCH at work. */
function dotWords(sinks: ChartSinks, ctx: ChartContext, d: DrawnPoint, fig: Fig, a: number): void {
  const t = ctx.cfg.pieces.points_of_interest;
  const spent = d.state === 'spent' && d.kind !== 'gate';
  let main = '', sub = '';
  const face = faceOf(ctx.reading);
  switch (d.kind) {
    case 'vent':
      // The stock is the figure; a worked-out vent states its refill.
      if (spent && d.left > 0) main = S.poi_dot_vent_left({ left: leftSpan(d.left) });
      else main = d.stock >= 0 ? S.poi_dot_vent_stock({ n: d.stock }) : S.poi_dot_vent;
      break;
    case 'cache': main = spent ? S.poi_dot_dug : S.poi_dot_cache; break;
    case 'lair':
      main = S.poi_dot_lair;
      if (d.state === 'worked') sub = S.poi_dot_empty;
      if (spent) sub = d.left > 0 ? leftSpan(d.left) : S.poi_dot_empty;
      break;
    case 'gate':
      main = S.poi_dot_gate;
      if (d.state === 'worked') sub = S.poi_dot_slack({ mul: mulText(d.tide) });
      else if (d.tide > 0) sub = S.poi_dot_flood({ mul: mulText(d.tide) });
      else sub = S.poi_dot_ebb({ mul: mulText(d.tide) });
      break;
    default:
      main = d.state === 'worked' ? S.poi_dot_ruin : S.poi_dot_ruin_cores({ n: spent ? 0 : 1 });
      break;
  }
  const x = d.x + fig.dot_label_dx, y = d.y + fig.dot_label_dy;
  const pivot = { x, y };
  const w = ctx.glyphs.word(sinks.quads, main, x, y, face, t.text.note, CHART_INK.pale,
    (spent ? fig.dot_spent_alpha : fig.dot_main_alpha) * a, { anchor: 'left', pivot });
  if (sub) {
    ctx.glyphs.word(sinks.quads, ` ${sub}`, x + w, y, face, t.text.note, CHART_INK.dim, a, { anchor: 'left', pivot });
  }
  if (d.state === 'worked' && d.kind !== 'gate') {
    const by = d.y + fig.dot_bar_dy;
    sinks.segments.line(x, by, x + fig.dot_bar_len, by, fig.dot_bar_w, CHART_INK.accent, fig.dot_bar_alpha * a);
  }
}

/** The water of a current gate by ring, ring 0 the cell: the set of
 *  `gateWater`, the server's `gateCells`, with each cell in the ring of its
 *  walk distance. A ridge stops the walk; a trench cell is water (R2958). */
export function gateRings(cell: number, steps: number, blocked: Uint8Array | null): number[][] {
  const rock = (c: number): boolean => blocked !== null && blocked[c] === BLOCK_RIDGE;
  if (cell < 0 || (blocked !== null && cell >= blocked.length) || rock(cell)) return [];
  const seen = new Set<number>([cell]);
  const rings: number[][] = [[cell]];
  for (let k = 1; k <= steps; k++) {
    const next: number[] = [];
    for (const c of rings[k - 1]) {
      for (let e = 0; e < 6; e++) {
        const n = neighborAt(c, e);
        if (n < 0 || seen.has(n) || rock(n)) continue;
        seen.add(n);
        next.push(n);
      }
    }
    next.sort((p, q) => p - q);
    rings.push(next);
  }
  return rings;
}

/** The reach of REACH (card 08) a cell cannot hold: the home of a monster
 *  round its lair, and the water of a current gate with its arrows. */
function reachUnder(sinks: ChartSinks, ctx: ChartContext, d: DrawnPoint, fig: Fig, a: number): void {
  const rules = poiRules();
  if (d.kind === 'lair') {
    // The home fades while the monster is out or gone (R2735).
    const r = rules.lairHomePx, home = d.state === 'ready';
    sinks.circles.disc(d.x, d.y, r, POI_INKS.npc, (home ? fig.reach_lair_fill : fig.reach_lair_fill_away) * a);
    sinks.circles.dashed(d.x, d.y, r, fig.reach_width, fig.reach_lair_dashes, LAIR_DASH_ON, POI_INKS.npc,
      (home ? fig.reach_lair_ring : fig.reach_lair_ring_away) * a);
    return;
  }
  if (d.kind !== 'gate') return;
  const ink = POI_INKS.neutral;
  const hex = ctx.glyphs.mark(POI_HEX_KEY);
  const k = (HEX_SIZE - fig.reach_cell_inset) / HEX_SIZE;
  const slack = d.state === 'worked';
  const L = fig.reach_arrow_px + fig.reach_arrow_per_tide * Math.abs(d.tide);
  const ux = Math.cos(d.rot), uy = Math.sin(d.rot);
  const rings = gateRings(d.cell, rules.gateReachCells, ctx.scene.blocked);
  rings.forEach((ring, step) => {
    for (const c of ring) {
      const cx = cellCenterX(c), cy = cellCenterY(c);
      sinks.quads.sprite(cx, cy, 0, hex, ink, fig.reach_cell_fill * a, k, 0, false);
      for (let e = 0; e < 6; e++) {
        const [ax, ay] = hexCorner(e), [bx, by] = hexCorner((e + 1) % 6);
        sinks.segments.line(cx + ax * k, cy + ay * k, cx + bx * k, cy + by * k, 1, ink, fig.reach_cell_edge * a);
      }
      if (step === 0 || step > fig.reach_arrow_rings) continue;
      if (slack) { sinks.circles.disc(cx, cy, fig.reach_slack_px, ink, fig.reach_slack_alpha * a); continue; }
      const aa = fig.reach_arrow_alpha * a, h = fig.reach_arrow_head;
      const tx = cx + ux * L, ty = cy + uy * L;
      sinks.segments.line(cx - ux * L, cy - uy * L, tx, ty, fig.reach_width, ink, aa);
      // The head: two strokes back from the tip, each half a head across.
      const bx0 = tx - ux * h, by0 = ty - uy * h;
      sinks.segments.line(bx0 - uy * h * 0.83, by0 + ux * h * 0.83, tx, ty, fig.reach_width, ink, aa);
      sinks.segments.line(bx0 + uy * h * 0.83, by0 - ux * h * 0.83, tx, ty, fig.reach_width, ink, aa);
    }
  });
}

/**
 * The monster kind of a lair (R2719, ui-map-interface.md §6.1.15): the
 * still mark of its kind beside the lair mark, in every set, and the name
 * of its design under the point. A world without `pirates` sends no kind,
 * so its lair draws as before.
 */
function lairKind(sinks: ChartSinks, ctx: ChartContext, d: DrawnPoint, fig: Fig, a: number): void {
  const pen = new BatchPen(sinks.segments, sinks.circles, null, null);
  pen.place(d.x + fig.lair_kind_dx, d.y + fig.lair_kind_dy, 0, fig.lair_kind_scale);
  pen.ew = 0.9;
  pen.alpha = fig.lair_kind_alpha * a;
  drawKindMark(d.monster, pen);
  const name = monsterDesignWord(d.monster, d.design);
  if (name) {
    ctx.glyphs.word(sinks.quads, name, d.x, d.y + fig.lair_design_dy, faceOf(ctx.reading),
      ctx.cfg.pieces.points_of_interest.text.note, CHART_INK.pale, fig.abbr_note_alpha * a, { anchor: 'centre' });
  }
}

/** The static half: the baked marks, the words and the reach. */
export class PointsOfInterestPiece implements Piece {
  readonly id = 'points_of_interest' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'static' as const;
  /** The survey is faction knowledge of today: the film hides it. */
  readonly film = 'hide' as const;
  // A timer writes again on a world minute through `outgrown`, only while
  // one stands, so the pack takes no reason 'minute'.
  readonly rebuildOn: readonly RebuildReason[] = ['hello', 'survey', 'reading', 'look', 'tide', 'state', 'font'];

  constructor(readonly model: PoiModel = new PoiModel()) {}

  /** The points of the last pack. The tests read them. */
  get drawn(): readonly DrawnPoint[] { return this.model.points; }

  visible(ctx: ChartContext): boolean {
    return ctx.state.survey.pois.length > 0 || ctx.state.adminCaches.length > 0;
  }

  rebuild(ctx: ChartContext): void {
    this.model.build(ctx);
    this.pickHalf = ctx.cfg.pieces.points_of_interest.fig.pick_half_px;
  }

  /** The half side of the hit square of the last pack, in world px. */
  private pickHalf = 0;

  /**
   * Offers the point of the survey whose hit square, grown by `margin`
   * world px, holds a world point (R2851): one scan, no allocation.
   */
  markPick(wx: number, wy: number, margin: number, out: SmallMark): void {
    const h = this.pickHalf;
    for (const d of this.model.points) {
      if (!d.poi || !boxHolds(wx, wy, d.x, d.y, -h, -h, h, h, margin)) continue;
      if (offerSmallMark(out, 'poi', d.x, d.y, wx, wy)) out.poi = d.poi;
    }
  }

  outgrown(ctx: ChartContext): boolean { return this.model.outgrown(ctx); }

  emit(sinks: ChartSinks, ctx: ChartContext): void {
    const t = ctx.cfg.pieces.points_of_interest;
    const fig = t.fig;
    const a = t[ctx.reading].alpha;
    const set = this.model.set;
    for (const d of this.model.points) {
      if (set === 'reach') reachUnder(sinks, ctx, d, fig, a);
      if (!poiBakesNothing(set, d.kind)) {
        const cell = ctx.glyphs.mark(poiCellKey(set, d.kind, d.state, d.m));
        sinks.quads.sprite(d.x, d.y, d.rot, cell, WHITE, a, 1, 0, false);
      }
      if (d.monster >= 0) lairKind(sinks, ctx, d, fig, a);
      if (set === 'chart-abbreviation') abbrWords(sinks, ctx, d, fig, a);
      else if (set === 'dot-and-label') dotWords(sinks, ctx, d, fig, a);
      else if (set === 'sea-bed') {
        if (this.model.small) {
          sinks.circles.disc(d.x, d.y, fig.bed_dot_px, CHART_INK.pale, fig.bed_dot_alpha * a, F_SCREEN);
        }
        leftFigure(sinks, ctx, d, t.arr.bed_left_dy, d.kind === 'vent' ? POI_INKS.pale : POI_INKS.npc, a);
      } else if (set === 'reach') leftFigure(sinks, ctx, d, t.arr.reach_left_dy, poiHue(d.kind, POI_INKS), a);
      else leftFigure(sinks, ctx, d, t.arr.grammar_left_dy, poiHue(d.kind, POI_INKS), a);
    }
  }
}

// ---------------------------------------------------------------------------
// The live object
// ---------------------------------------------------------------------------

/** One dashed polyline whose dashes run `offset` px along it: the stream
 *  of a SEA BED gate. `pts` are local points, turned by `ux, uy` and placed
 *  at `ox, oy`. */
function dashedStream(
  sinks: ChartSinks, pts: readonly number[], ox: number, oy: number, ux: number, uy: number,
  on: number, off: number, offset: number, width: number, ink: number, alpha: number,
): void {
  const period = on + off;
  let s0 = ((offset % period) + period) % period - period;
  let walked = 0;
  for (let i = 0; i + 3 < pts.length; i += 2) {
    const x0 = pts[i], y0 = pts[i + 1], x1 = pts[i + 2], y1 = pts[i + 3];
    const len = Math.hypot(x1 - x0, y1 - y0);
    if (len === 0) continue;
    while (s0 < walked + len) {
      const a = Math.max(s0, walked), b = Math.min(s0 + on, walked + len);
      if (b > a) {
        const ta = (a - walked) / len, tb = (b - walked) / len;
        const lx0 = x0 + (x1 - x0) * ta, ly0 = y0 + (y1 - y0) * ta;
        const lx1 = x0 + (x1 - x0) * tb, ly1 = y0 + (y1 - y0) * tb;
        sinks.segments.line(ox + lx0 * ux - ly0 * uy, oy + lx0 * uy + ly0 * ux,
          ox + lx1 * ux - ly1 * uy, oy + lx1 * uy + ly1 * ux, width, ink, alpha);
      }
      if (s0 + period >= walked + len) break;
      s0 += period;
    }
    walked += len;
  }
}

/** The stream lines of a SEA BED gate, pinched through the narrows, in
 *  local px, pointing east: x, y pairs. */
function streamLines(fig: Fig): number[][] {
  const out: number[][] = [];
  const half = fig.bed_stream_half_px, m = (fig.bed_stream_lines - 1) / 2;
  for (let i = -m; i <= m; i++) {
    const line: number[] = [];
    for (let x = -half; x <= half; x += fig.bed_stream_step_px) {
      const k = x / half;
      line.push(x, i * fig.bed_stream_gap_px * (fig.bed_stream_pinch + fig.bed_stream_flare * k * k));
    }
    out.push(line);
  }
  return out;
}

/** The live half: what moves in SEA BED and in SHAPE GRAMMAR. */
export class PointsOfInterestLivePiece implements Piece {
  readonly id = 'points_of_interest' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'live' as const;
  readonly film = 'hide' as const;
  readonly rebuildOn: readonly RebuildReason[] = ['hello', 'survey', 'look', 'tide', 'state'];
  private lines: number[][] = [];
  private linesFig: Fig | null = null;

  constructor(readonly model: PoiModel = new PoiModel()) {}

  visible(ctx: ChartContext): boolean {
    return (ctx.state.survey.pois.length > 0 || ctx.state.adminCaches.length > 0)
      && LIVE_SETS.has(ctx.scene.poi ?? AUTO_SET);
  }

  rebuild(ctx: ChartContext): void { this.model.build(ctx); }

  emit(sinks: ChartSinks, ctx: ChartContext, frame: FrameInfo): void {
    const t = ctx.cfg.pieces.points_of_interest;
    const fig = t.fig;
    const a = t[ctx.reading].alpha;
    const set = this.model.set;
    if (!LIVE_SETS.has(set)) return;
    const time = this.model.still ? 0 : frame.timeS;
    if (this.linesFig !== fig) {
      this.lines = streamLines(fig);
      this.linesFig = fig;
    }
    for (const d of this.model.points) {
      if (offCull(frame.cull, d.x, d.y, CULL_MARGIN)) continue;
      if (set === 'sea-bed') this.seaBed(sinks, d, fig, a, time);
      else if (d.state === 'worked' && (d.kind === 'cache' || d.kind === 'ruin')) {
        // The own-cyan dot runs round a take point under a SEARCH.
        const turn = fig.grammar_orbit_sec > 0 ? (time / fig.grammar_orbit_sec) % 1 : 0;
        const ang = -Math.PI / 2 + turn * 2 * Math.PI, r = fig.grammar_orbit_px;
        const trail = fig.grammar_trail * 2 * Math.PI;
        sinks.circles.arc(d.x, d.y, r, fig.grammar_trail_w, ang - trail, trail, CHART_INK.accent,
          fig.grammar_trail_alpha * a);
        sinks.circles.disc(d.x + Math.cos(ang) * r, d.y + Math.sin(ang) * r, fig.grammar_dot_px, CHART_INK.accent, a);
      }
    }
  }

  /** The specks of a vent, the flash of a cache and the stream of a gate. */
  private seaBed(sinks: ChartSinks, d: DrawnPoint, fig: Fig, a: number, time: number): void {
    if (d.kind === 'vent') {
      // A worked-out vent sends up no speck; a low one sends faint ones.
      if (d.state === 'spent') return;
      const k = fig.bed_speck_low + fig.bed_speck_share * d.share;
      // A plain loop, so a frame makes no closure (§10.3).
      for (let i = 0; i < d.specks.length; i++) {
        const sp = d.specks[i];
        const q = (time * fig.bed_rise_per_sec + sp.ph) % 1;
        const x = d.x + sp.x0 + Math.sin(q * fig.bed_speck_sway + i) * fig.bed_speck_sway_px, y = d.y - q * fig.bed_rise_px;
        sinks.circles.disc(x, y, fig.bed_speck_px + fig.bed_speck_grow_px * (1 - q), POI_INKS.iso,
          fig.bed_speck_alpha * (1 - q) * k * a);
      }
    } else if (d.kind === 'cache' && d.state !== 'spent') {
      const per = d.state === 'worked' ? fig.bed_glint_work_sec : fig.bed_glint_sec;
      const ph = per > 0 ? (time % per) / per : 1;
      const fl = ph < fig.bed_glint_flash ? 1 - ph / fig.bed_glint_flash : 0;
      if (fl <= 0) return;
      const gx = d.x + fig.bed_glint_dx, gy = d.y + fig.bed_glint_dy, r = fig.bed_glint_r0_px + fig.bed_glint_grow_px * fl;
      sinks.circles.disc(gx, gy, fig.bed_glint_core_px, POI_INKS.credit, fig.bed_glint_core_alpha * fl * a);
      const w = fig.bed_glint_width, ga = fig.bed_glint_alpha * fl * a;
      sinks.segments.line(gx - r, gy, gx + r, gy, w, POI_INKS.credit, ga);
      sinks.segments.line(gx, gy - r, gx, gy + r, w, POI_INKS.credit, ga);
    } else if (d.kind === 'gate') {
      const slack = d.state === 'worked', mag = Math.abs(d.tide);
      const alpha = (slack ? fig.bed_stream_slack_alpha : fig.bed_stream_alpha + fig.bed_stream_tide_alpha * mag) * a;
      const offset = slack ? 0 : time * fig.bed_stream_px_per_sec * mag;
      const ux = Math.cos(d.rot), uy = Math.sin(d.rot);
      for (const pts of this.lines) {
        dashedStream(sinks, pts, d.x, d.y, ux, uy,
          slack ? fig.bed_stream_slack_on : fig.bed_stream_on, slack ? fig.bed_stream_slack_off : fig.bed_stream_off, offset,
          fig.bed_stream_width, POI_INKS.neutral, alpha);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// The area of a current gate
// ---------------------------------------------------------------------------

/**
 * The water of a current gate (R2955): the port of `gateCells` of
 * `server/internal/world/currents.go`. A breadth-first walk from the cell
 * takes each cell within `steps` hex steps that it reaches through cells
 * that are not ridge; a trench cell is water. A ridge cell or a cell off
 * the map gives none. The cell comes first.
 */
export function gateWater(cell: number, steps: number, blocked: Uint8Array | null): number[] {
  const rock = (c: number): boolean => blocked !== null && blocked[c] === BLOCK_RIDGE;
  if (cell < 0 || (blocked !== null && cell >= blocked.length) || rock(cell)) return [];
  const dist = new Map<number, number>([[cell, 0]]);
  const queue = [cell];
  for (let q = 0; q < queue.length; q++) {
    const c = queue[q], k = dist.get(c) ?? 0;
    if (k >= steps) continue;
    for (let e = 0; e < 6; e++) {
      const n = neighborAt(c, e);
      if (n < 0 || dist.has(n) || rock(n)) continue;
      dist.set(n, k + 1);
      queue.push(n);
    }
  }
  return queue;
}

/** The current gate whose card the peek shows, or undefined. */
function peekedGate(ctx: ChartContext): PointOfInterest | undefined {
  if (ctx.peekPoi === 0) return undefined;
  const p = ctx.state.survey.poi(ctx.peekPoi);
  return p && p.kind === PoiKind.POI_KIND_GATE ? p : undefined;
}

/**
 * The area of ONE current gate (R2955, §17.3.69): the gate whose card the
 * peek shows, after a hover or a press on its mark, as a dashed hex
 * outline in the gate ink. It follows the harbour limits of card 28, and
 * it draws under every set of the Marks field. It writes no word: the peek
 * card names the gate.
 */
export class PoiGateAreaPiece implements Piece {
  readonly id = 'points_of_interest' as const;
  /** The screen tier, with card 28: a hover repacks this tier. */
  readonly tier: ChartTier = 'screen';
  readonly pack = 'static' as const;
  readonly film = 'hide' as const;
  /** The dash is the dash of the reading, so a reading repacks too. */
  readonly rebuildOn: readonly RebuildReason[] = ['hello', 'survey', 'hover', 'reading'];

  /** The outline of the last pack. The tests read it. */
  strokes: Stroke[] = [];
  /** What the outline was built from, so a hover that keeps the gate
   *  repacks without a new walk. */
  private cell = -1;
  private steps = -1;
  private blocked: Uint8Array | null = null;

  visible(ctx: ChartContext): boolean { return peekedGate(ctx) !== undefined; }

  rebuild(ctx: ChartContext): void {
    const p = peekedGate(ctx);
    if (!p) { this.strokes = []; this.cell = -1; return; }
    const steps = poiRules().gateReachCells, blocked = ctx.scene.blocked;
    if (p.cell === this.cell && steps === this.steps && blocked === this.blocked) return;
    this.cell = p.cell;
    this.steps = steps;
    this.blocked = blocked;
    this.strokes = outlineStrokes(gateWater(p.cell, steps, blocked));
  }

  emit(sinks: ChartSinks, ctx: ChartContext): void {
    if (this.strokes.length === 0) return;
    const t = ctx.cfg.pieces.points_of_interest;
    dashedStrokes(sinks.segments, this.strokes, t.fig.gate_area_width, POI_INKS.neutral,
      t.fig.gate_area_alpha, t[ctx.reading].dash);
  }
}

const model = new PoiModel();
/** The three instances the registry holds. */
export const pointsOfInterest = new PointsOfInterestPiece(model);
export const pointsOfInterestLive = new PointsOfInterestLivePiece(model);
export const pointsOfInterestGate = new PoiGateAreaPiece();
